import { cloneDeep, get } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import { pruneEntityErrors, pruneFieldError } from '@/utils/errors';
import pagingWithFilteredResults, {
  presentationOptions,
  visibilityFilters,
  entityVisibility,
} from '@/constants/states';
import { readStates, states } from '@/constants/lifecycle';
import { isCommonsModeEnabled } from '@/auth/featureToggles';
import { datasetSettingsFields } from '@/datasets/settings/state/state';
import activeClause from '../clauses/state/state';
import {
  activeQuery,
  addUUIDsToQueries,
  mergeQueries,
  removeUUIDsFromQueries,
} from '../queries/state/state';
import sharingCohort, { cohortPerms } from '../sharing/state/state';
import { cohortStates } from '../constants';
import cohortStatusInitialState from '../status/state';
import { cohortVisualisationState } from '../visualisations/state/state';

export const cohortDatasets = (privateFlag, internalFlag, publicFlag) => (
  {
    ...pagingWithFilteredResults(),
    ...readStates,
    ...visibilityFilters(privateFlag, internalFlag, publicFlag),
    multiSelect: false,
  }
);

export const cohortDictionaries = () => (
  {
    ...pagingWithFilteredResults(),
    ...readStates,
    multiSelect: true,
  }
);

export const openCohort = (code, can = false, readStatus = states.PENDING) => ({
  code,
  readStatus,
  readErrors: [],
  queries: [],
  visualisations: cohortVisualisationState(),
  permissions: cohortPerms(can),
  status: cohortStatusInitialState(),
  access: { query_access: true, dataset_access: true },
  settings: datasetSettingsFields(),
  expandedCharts: false,
  copy: {},
});

export const newCohortState = (code, selectSource) => ({
  ...openCohort(code, true),
  isNew: true,
  isEditing: true,
  isDirty: true,
  id: null,
  name: '',
  description: '',
  visibility: entityVisibility.PRIVATE,
  copy: { name: '', queries: [], description: '' },
  datasets: cohortDatasets(true, true, isCommonsModeEnabled()),
  dictionaries: cohortDictionaries(),
  selectSource,
  ...cohortStates,
});

const savedCohorts = (privateFlag, internalFlag, display = presentationOptions.TABLE) => (
  {
    saved: {
      ...pagingWithFilteredResults(),
      ...cohortStates,
      ...visibilityFilters(privateFlag, internalFlag),
      display,
      multiSelect: true,
    },
  }
);

export const mergeCohort = (source, target) => {
  if (!target.queries) {
    return target.queries;
  }

  const queries = addUUIDsToQueries(source, target);
  return (queries.length > 0) ? mergeQueries(source, target, queries) : queries;
};

const initialState = {
  newCohortCode: uuidv4(),
  deleteCohortOpen: false,
  activeCohort: { open: false },
  activeQuery: { ...activeQuery() },
  activeClause: { ...activeClause() },
  sharingCohort: { ...sharingCohort() },
  cohorts: {
    ...savedCohorts(true, false),
    ribbon: {
      params: ['favourites'],
      ...pagingWithFilteredResults(5),
      ...visibilityFilters(true, true),
      ...cohortStates,
    },
    open: {
      results: [],
    },
    deletion: {
      results: [],
    },
    system: {
      results: [],
    },
  },
};

export const supportedCohortVisibilities = (item) => {
  const setting = item?.settings || datasetSettingsFields();
  const visibilities = [];
  if (setting.allow_private_cohorts) {
    visibilities.push(entityVisibility.PRIVATE);
  }

  if (setting.allow_internal_cohorts) {
    visibilities.push(entityVisibility.INTERNAL);
  }
  return visibilities;
};

export const newCohortProperties = (item) => {
  const datasetCohortVisibilities = supportedCohortVisibilities(item);
  const visibility = datasetCohortVisibilities.includes(item.visibility)
    ? item.visibility
    : datasetCohortVisibilities[0];

  const copy = cloneDeep(item);
  return ({
    ...copy,
    visibility: visibility || entityVisibility.PRIVATE,
    code: null,
    uuid: copy.code,
    queries: removeUUIDsFromQueries(copy),
    open: true,
    datasetCohortVisibilities,
  });
};

export const cohortStateToLoad = ({ cohorts }) => {
  const items = get(cohorts, 'cohorts', initialState.cohorts);
  const savedFilters = items.saved || initialState.cohorts.saved;

  return {
    ...initialState,
    cohorts: {
      ...initialState.cohorts,
      ...savedCohorts(savedFilters.privateFlag, savedFilters.internalFlag, savedFilters.display),
    },
  };
};

export const cohortStateToSave = ({ cohorts }) => (
  {
    cohorts: {
      cohorts: {
        saved: {
          privateFlag: cohorts.cohorts.saved.privateFlag,
          internalFlag: cohorts.cohorts.saved.internalFlag,
          display: cohorts.cohorts.saved.display,
        },
      },
    },
  }
);

const dictionaryQueryErrorNotMatch = (error, queryUUID, dictionaryCode) => (
  error.context.queryUUID !== queryUUID || error.context.dictionaryCode !== dictionaryCode
);

const dictionaryQueryFieldErrorNotMatch = (error, queryUUID, dictionaryCode, field = null) => (
  error.context.queryUUID === queryUUID
      && error.context.dictionaryCode === dictionaryCode
      && pruneFieldError(error, field)
);

export const pruneCohortDictionaryErrors = (errors, { queryUUID, dictionaryCode }, field = null) => (
  pruneEntityErrors(errors, ['clause'], (e) => dictionaryQueryErrorNotMatch(e, queryUUID, dictionaryCode)
      || dictionaryQueryFieldErrorNotMatch(e, queryUUID, dictionaryCode, field))
);

export default initialState;
